import * as React from "react";

import { useWebSocket } from "@/lib/cjs-interop";
import { DOCUMENT_AUTHORING_BASE_URL } from "../documentAuthoring.constraints";
import type { AuthoringRunFrame } from "../documentAuthoring.types";

const FRAME_TYPES = new Set<AuthoringRunFrame["type"]>([
  "start",
  "check_done",
  "check_failed",
  "applying",
  "repairing",
  "done",
  "error",
]);

/** Parses one socket message; anything not matching the contract is dropped. */
export const parseAuthoringRunFrame = (data: unknown): AuthoringRunFrame | null => {
  if (typeof data !== "string") return null;
  try {
    const parsed: unknown = JSON.parse(data);
    if (typeof parsed !== "object" || parsed === null) return null;
    const frame = parsed as { type?: unknown };
    if (typeof frame.type !== "string" || !FRAME_TYPES.has(frame.type as AuthoringRunFrame["type"])) return null;
    return frame as AuthoringRunFrame;
  } catch {
    return null;
  }
};

export const authoringRunSocketUrl = (runId: string): string =>
  `${DOCUMENT_AUTHORING_BASE_URL}/ws/${encodeURIComponent(runId)}`;

/**
 * Streams `WS /pf-mainweb/document-authoring/ws/{run_id}` frames while `runId` is set. The
 * backend replays the stream from id 0, so a reconnect re-delivers earlier frames; the
 * reducer consuming them is idempotent per index.
 */
export const useAuthoringRunSocket = (runId: string | null, onFrame: (frame: AuthoringRunFrame) => void): void => {
  const onFrameRef = React.useRef(onFrame);
  onFrameRef.current = onFrame;

  useWebSocket(
    runId ? authoringRunSocketUrl(runId) : null,
    {
      onMessage: event => {
        const frame = parseAuthoringRunFrame(event.data);
        if (frame) onFrameRef.current(frame);
      },
      // 1000 is the server's normal close after `done`/`error`; 1008 is a policy rejection
      // (not the caller's run) and must not be retried.
      shouldReconnect: event => event?.code !== 1000 && event?.code !== 1008,
      reconnectInterval: 3000,
      skipAssert: true,
    },
    runId !== null,
  );
};
